// Pure rule-building helpers for context/personalization conditions
// (ModalCreateContextItem.vue and, going forward, the adaptive-journeys
// condition editor). Extracted so operator selection and json-logic
// construction are unit-testable without mounting a Vue component
// (this area is Vue 2 / Jest, not editor-v2 / vitest).
//
// The field-type -> allowed-operator mapping mirrors `comparatorsFor` in
// src/editor-v2/forms/logicEditor.helpers.ts so form logic and conditions
// offer the same possibilities.

// Field types are grouped the same way comparatorsFor groups them; unknown
// or unlisted types fall back to the 'text' group.
const BOOLEAN_FIELD_TYPES = ['boolean', 'consent'];
const CHECKBOX_FIELD_TYPES = ['checkbox'];
const CHOICE_FIELD_TYPES = ['radio', 'select'];
const NUMBER_FIELD_TYPES = ['number', 'rating', 'nps', 'slider'];

function groupFor(fieldType) {
  if (BOOLEAN_FIELD_TYPES.includes(fieldType)) return 'boolean';
  if (CHECKBOX_FIELD_TYPES.includes(fieldType)) return 'checkbox';
  if (CHOICE_FIELD_TYPES.includes(fieldType)) return 'choice';
  if (NUMBER_FIELD_TYPES.includes(fieldType)) return 'number';
  return 'text';
}

// Operators carry a translation KEY, not a translated label, so this module
// stays free of `this.$t` and the component translates on display.
export const OPERATORS = [
  {
    id: 'equal',
    jl: '==',
    labelKey: 'settings.page.context.condition.operatorOptions.isEqual',
    needsValue: true,
    types: ['boolean', 'choice', 'number', 'text'],
  },
  {
    id: 'notEqual',
    jl: '!=',
    labelKey: 'settings.page.context.condition.operatorOptions.notEqual',
    needsValue: true,
    types: ['choice', 'number', 'text'],
  },
  {
    id: 'contains',
    jl: 'in',
    labelKey: 'settings.page.context.condition.operatorOptions.contains',
    needsValue: true,
    types: ['checkbox', 'text'],
  },
  {
    id: 'greaterThan',
    jl: '>',
    labelKey: 'settings.page.context.condition.operatorOptions.greaterThan',
    needsValue: true,
    types: ['number'],
  },
  {
    id: 'greaterThanOrEqual',
    jl: '>=',
    labelKey: 'settings.page.context.condition.operatorOptions.greaterThanOrEqual',
    needsValue: true,
    types: ['number'],
  },
  {
    id: 'lessThan',
    jl: '<',
    labelKey: 'settings.page.context.condition.operatorOptions.lessThan',
    needsValue: true,
    types: ['number'],
  },
  {
    id: 'lessThanOrEqual',
    jl: '<=',
    labelKey: 'settings.page.context.condition.operatorOptions.lessThanOrEqual',
    needsValue: true,
    types: ['number'],
  },
  {
    id: 'answered',
    jl: '!!',
    labelKey: 'settings.page.context.condition.operatorOptions.answered',
    needsValue: false,
    types: ['boolean', 'checkbox', 'choice', 'number', 'text'],
  },
  {
    id: 'notAnswered',
    jl: '!',
    labelKey: 'settings.page.context.condition.operatorOptions.notAnswered',
    needsValue: false,
    types: ['boolean', 'checkbox', 'choice', 'number', 'text'],
  },
];

// True for the field types whose only two possible answers are true and
// false — the ones that need a Ja/Nein picker instead of a free text box,
// and the ones coerceValue below turns into real booleans. Exported so the
// value input and the coercion can never disagree about which types those
// are.
export function isBooleanFieldType(fieldType) {
  return BOOLEAN_FIELD_TYPES.includes(fieldType);
}

// The operators applicable to a given (concrete) field type, e.g. 'number',
// 'checkbox', 'boolean', 'text'.
export function operatorsFor(fieldType) {
  const group = groupFor(fieldType);
  return OPERATORS.filter((operator) => operator.types.includes(group));
}

// Turns a raw string value into the type a field's comparisons expect.
// A number field must produce a NUMBER, not a string — otherwise '>'
// compares strings and "9" is greater than "10".
export function coerceValue(fieldType, raw) {
  if (NUMBER_FIELD_TYPES.includes(fieldType)) {
    const n = Number(raw);
    if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(n)) return n;
    return raw;
  }
  if (BOOLEAN_FIELD_TYPES.includes(fieldType)) {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  }
  return raw;
}

// Builds the json-logic operation for a rule. `contains` (jl: 'in') reverses
// the arguments compared to the other operators: json-logic's `in` reads
// "needle in haystack", i.e. { in: [value, { var: path }] }. That is the
// only form that works for both plain text and multi-select fields.
export function buildOperation({
  path, operator, value, fieldType,
}) {
  // '!!' means "beantwortet". json-logic's own truthiness (jsonLogic.truthy)
  // calls the number 0 and the answer false empty, which is wrong for a form:
  // somebody who types 0 or ticks "Nein" HAS answered. So the old truthiness
  // check stays -- it is right about everything else, including the empty
  // array an untouched multi-select stores -- and the two values it judges
  // wrongly are added back explicitly. Strict '===' on purpose: json-logic's
  // loose '==' would make 0 equal '' and false equal '', which would drag the
  // empty text box back in.
  //
  // Only NEWLY built rules get this shape. A stored rule carries its finished
  // operation and keeps behaving exactly as before, which is deliberate:
  // silently changing what a live customer magazine shows would be worse than
  // the bug. parseOperation below reads both shapes.
  if (operator === '!!') {
    return {
      or: [
        { '!!': { var: path } },
        { '===': [{ var: path }, 0] },
        { '===': [{ var: path }, false] },
      ],
    };
  }
  if (operator === '!') {
    return {
      and: [
        { '!': { var: path } },
        { '!==': [{ var: path }, 0] },
        { '!==': [{ var: path }, false] },
      ],
    };
  }
  const coerced = coerceValue(fieldType, value);
  if (operator === 'in') {
    return { in: [coerced, { var: path }] };
  }
  return { [operator]: [{ var: path }, coerced] };
}

// Reads a json-logic operation back into { path, operator, value }, or null
// if it isn't a shape this module builds/recognizes. Mirrors buildOperation,
// including the reversed `in` arguments, and also reads the pre-existing
// personalization shape already stored for customers, e.g.
// { "==": [{ "var": "7.tevis" }, "1"] }.
export function parseOperation(op) {
  if (!op || typeof op !== 'object') return null;
  const keys = Object.keys(op);
  if (keys.length !== 1) return null;
  const [operator] = keys;

  // The shapes buildOperation writes for answered / not answered. Read BEFORE
  // the known-operator check below: 'or' and 'and' are not operators the
  // picker offers, so that check would turn them down first and a rule built
  // five minutes ago would reopen as "von Hand geschrieben".
  //
  // Only the EXACT shape buildOperation writes counts. A hand-written or/and
  // that merely starts the same way is left unread on purpose -- rebuilding
  // it from { path, operator } alone would drop its other parts, which is the
  // silent data loss this module exists to avoid.
  if (operator === 'or' || operator === 'and') {
    const wanted = operator === 'or' ? '!!' : '!';
    const parts = op[operator];
    if (!Array.isArray(parts) || parts.length !== 3) return null;
    const varRef = parts[0] && typeof parts[0] === 'object' ? parts[0][wanted] : null;
    if (!varRef || typeof varRef !== 'object' || typeof varRef.var !== 'string') return null;
    const canonical = buildOperation({ path: varRef.var, operator: wanted });
    if (JSON.stringify(op) !== JSON.stringify(canonical)) return null;
    return { path: varRef.var, operator: wanted, value: undefined };
  }

  if (!OPERATORS.some((o) => o.jl === operator)) return null;

  const rhs = op[operator];

  if (operator === '!!' || operator === '!') {
    if (!rhs || typeof rhs !== 'object' || !('var' in rhs)) return null;
    return { path: rhs.var, operator, value: undefined };
  }

  if (!Array.isArray(rhs) || rhs.length !== 2) return null;

  if (operator === 'in') {
    const [value, varRef] = rhs;
    if (!varRef || typeof varRef !== 'object' || !('var' in varRef)) return null;
    return { path: varRef.var, operator, value };
  }

  const [varRef, value] = rhs;
  if (!varRef || typeof varRef !== 'object' || !('var' in varRef)) return null;
  return { path: varRef.var, operator, value };
}

// ---------------------------------------------------------------------------
// Keeping a rule the editor did not touch exactly as it was found.
//
// buildOperation writes the CURRENT shape, and the current shape is not always
// the stored one: '!!' / '!' changed shape when the number 0 and the answer
// "Nein" started counting as answered. Reading a stored rule and writing it
// straight back would therefore migrate it -- silently, on an "Übernehmen" the
// editor pressed without changing anything. For every visitor who answered 0
// or ticked "Nein", the snippet would then appear or disappear where it did
// not before. Nothing may migrate by itself; only a row the editor really
// edited is rebuilt, and there the new shape is right and wanted.
//
// This is the promise the module already keeps for an operation it cannot read
// at all (carried through verbatim), one step finer: an operation it CAN read
// is carried through too, as long as it still says the same thing.
// ---------------------------------------------------------------------------

/**
 * Whether the value the row carries is still the value that was READ out of
 * the stored operation.
 *
 * Fix round 1 of task 3. The question this has to answer is "did the editor
 * change the value?", and it used to be answered by comparing the stored
 * literal against the REBUILT one -- which is the stored value after
 * coerceValue has had it. So a value nobody touched came back different for no
 * reason but its field's type:
 *
 *   stored { "==": [{ "var": "form.42.consent" }, "true"] } on a yes/no field
 *   rebuilds as `true`, the comparison said "changed", and the string was
 *   replaced by the boolean in storage. For json-logic that is a change of
 *   MEANING, not of notation: 'true' == true is false, true == true is true.
 *   The rule flipped for every visitor, and nobody had touched it. The same
 *   with "10000" on a number field.
 *
 * So the comparison is against the row instead. `Object.is` first, which is
 * exact and settles every caller that keeps its row value as the literal it
 * read (segmentConditionModel.js). A caller that keeps it as TEXT
 * (editor-v2/utils/conditionModel.ts does: String(parsed.value), because a
 * text box can hold nothing else) holds the string rendering of that same
 * literal, and that is the one further step forgiven here -- for a number,
 * boolean or string only, so that `null` and a typed "null" stay apart.
 *
 * Anything else is a real edit and is written new.
 */
function valueUnchanged(storedValue, rowValue) {
  // Object.is, not ===: it tells undefined (no value at all, as '!!' and '!'
  // have) apart from everything else without calling two different numbers
  // equal. It also keeps two separate arrays apart, so a hand-built `in`
  // carrying a list is only kept when the row still holds that very list.
  if (Object.is(storedValue, rowValue)) return true;
  if (typeof rowValue !== 'string') return false;
  const stored = typeof storedValue;
  if (stored !== 'number' && stored !== 'boolean' && stored !== 'string') return false;
  return rowValue === String(storedValue);
}

// The pre-existing comparison, for a caller that has no row to ask: the stored
// literal against the rebuilt one, exactly. A value of a different literal
// type counts as changed here -- which is right when the two operations are
// all there is to go on, and wrong the moment a row can be asked instead.
function rebuiltValueUnchanged(storedValue, rebuiltValue) {
  return Object.is(storedValue, rebuiltValue);
}

// The stored operation when the row still says exactly what it said, otherwise
// the rebuilt one. "The same thing" is path, comparison and value -- which is
// a row, whole.
//
// `value` is the row's own value, in whatever representation the caller keeps
// it (see valueUnchanged). A caller that has no row to ask -- the old page
// settings chain, which only ever sees two operations -- leaves it out and
// gets the pre-existing comparison against the rebuilt literal.
export function keepUnchangedOperation(original, rebuilt, value) {
  if (original === undefined || original === null) return rebuilt;
  const before = parseOperation(original);
  const after = parseOperation(rebuilt);
  if (!before || !after) return rebuilt;
  if (before.path !== after.path || before.operator !== after.operator) return rebuilt;
  // `undefined` is also what a valueless '!!' / '!' row carries, and there
  // after.value is undefined too -- both readings give the same answer, so the
  // fallback costs nothing on the one shape it cannot tell apart.
  const unchanged = value === undefined
    ? rebuiltValueUnchanged(before.value, after.value)
    : valueUnchanged(before.value, value);
  if (!unchanged) return rebuilt;
  return original;
}

// The same operation with `oldPath` swapped for `newPath` wherever it stands
// as a `var`, and everything else -- the operation's SHAPE above all -- left
// exactly as found.
//
// A caller that only moves a path must not go the parseOperation +
// buildOperation way to do it: that hands back the current shape and so
// migrates a stored rule as a side effect of moving its path. Which is what
// the segment panel did every time it took the "urlParameter." namespace off
// on open and put it back on save.
export function withVarPath(operation, oldPath, newPath) {
  if (Array.isArray(operation)) {
    return operation.map((part) => withVarPath(part, oldPath, newPath));
  }
  if (operation && typeof operation === 'object') {
    const out = {};
    Object.keys(operation).forEach((key) => {
      out[key] = key === 'var' && operation[key] === oldPath
        ? newPath
        : withVarPath(operation[key], oldPath, newPath);
    });
    return out;
  }
  return operation;
}
